/**
 * Coup de pouce, Attendu 1241 « connaître et utiliser les formules du
 * volume d'une pyramide ou d'un cône » (3e, Grandeurs). Seconde passe 3e
 * (2026-09-13). DEUX pages STATIQUES partagées par les quatre clés, le
 * même geste : le tiers du solide droit qui a la même base et la même
 * hauteur (programme cycle 4, l. 1075 : « le volume d'une pyramide, d'un
 * cône, est le tiers de celui du prisme, du cylindre de même base et de
 * même hauteur »), c'est ainsi que la formule a été construite en 4e (le
 * transvasement).
 *   « Le tiers du pavé » : une pyramide à base carrée de côté 10 et de
 *     hauteur 3 dans le pavé 10 × 10 × 3 = 300 cm³ ; la pyramide en prend
 *     le tiers : 100 cm³.
 *   « Le tiers du cylindre » : un cône de rayon 10 et de hauteur 3 dans le
 *     cylindre π × 10 × 10 × 3 = 300π cm³ ; le cône en prend le tiers :
 *     100π cm³.
 *
 * EXEMPLES HORS PLAGE : côté 10 et rayon 10 (les cartes vont de 2 à 6),
 * vérifié par cdp3e-lot3.self.test.js.
 */
import { svg } from './1054';

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';
const GRIS = '#64748B';
const BLEU_CLAIR = '#DBEAFE';

const ligne = (y, contenu, taille = 12, couleur = ENCRE) => `<text x="200" y="${y}" font-size="${taille}" text-anchor="middle" fill="${couleur}">${contenu}</text>`;

// ─── Page 1 : le pavé et la pyramide ───────────────────────────────────────
// Pavé en perspective cavalière : face avant 120 × 36, fuyante 0,5 à 45°.
const A = { x: 60, y: 150 }; const B = { x: 180, y: 150 }; const C = { x: 180, y: 114 }; const D = { x: 60, y: 114 };
const f = (p) => ({ x: p.x + 42, y: p.y - 42 });
const Af = f(A); const Bf = f(B); const Cf = f(C); const Df = f(D);
const seg = (P, Q, opts = '') => `<line x1="${P.x}" y1="${P.y}" x2="${Q.x}" y2="${Q.y}" stroke="${ENCRE}" stroke-width="1.4" ${opts}/>`;
const cache = 'stroke-dasharray="4 3" stroke-width="1.1"';
const SOMMET = { x: (D.x + Cf.x) / 2, y: (D.y + Cf.y) / 2 };
const PAVE = [
    `<polygon points="${A.x},${A.y} ${B.x},${B.y} ${Bf.x},${Bf.y} ${Af.x},${Af.y}" fill="${BLEU_CLAIR}" stroke="none"/>`,
    seg(A, B), seg(B, C), seg(C, D), seg(D, A), seg(B, Bf), seg(C, Cf), seg(D, Df), seg(Bf, Cf), seg(Cf, Df), seg(A, Af, cache), seg(Af, Bf, cache), seg(Af, Df, cache),
    `<line x1="${A.x}" y1="${A.y}" x2="${SOMMET.x}" y2="${SOMMET.y}" stroke="${ORANGE}" stroke-width="1.8"/>`,
    `<line x1="${B.x}" y1="${B.y}" x2="${SOMMET.x}" y2="${SOMMET.y}" stroke="${ORANGE}" stroke-width="1.8"/>`,
    `<line x1="${Bf.x}" y1="${Bf.y}" x2="${SOMMET.x}" y2="${SOMMET.y}" stroke="${ORANGE}" stroke-width="1.8"/>`,
    `<line x1="${Af.x}" y1="${Af.y}" x2="${SOMMET.x}" y2="${SOMMET.y}" stroke="${ORANGE}" stroke-width="1.4" stroke-dasharray="4 3"/>`,
    `<circle cx="${SOMMET.x}" cy="${SOMMET.y}" r="3" fill="${ORANGE}"/>`,
    `<text x="${(A.x + B.x) / 2}" y="${A.y + 16}" font-size="12" font-weight="bold" text-anchor="middle" fill="${BLEU}">10</text>`,
    `<text x="${Bf.x + 10}" y="${(B.y + Bf.y) / 2 + 4}" font-size="12" font-weight="bold" fill="${BLEU}">10</text>`,
    `<text x="${B.x + 8}" y="${(B.y + C.y) / 2 + 4}" font-size="12" font-weight="bold" fill="${GRIS}">3</text>`,
].join('\n  ');

const SVG_PAVE = svg(
    'Un pavé droit de base carrée 10 sur 10 et de hauteur 3, et dedans une pyramide orange qui a la même base et la même hauteur. Le pavé : 10 fois 10 fois 3 égale 300 centimètres cubes. La pyramide en prend le tiers : 300 divisé par 3 égale 100 centimètres cubes. Volume de la pyramide égale aire de la base fois hauteur divisé par 3.',
    `${PAVE}
  <text x="250" y="84" font-size="12" fill="${ENCRE}">le pavé :</text>
  <text x="250" y="100" font-size="12" fill="${ENCRE}"><tspan fill="${BLEU}" font-weight="bold">10 × 10</tspan> × <tspan fill="${GRIS}" font-weight="bold">3</tspan> = 300 cm³</text>
  <text x="250" y="126" font-size="12" fill="${ORANGE}">la pyramide : <tspan font-weight="bold">le tiers</tspan></text>
  <text x="250" y="142" font-size="12" fill="${ORANGE}">300 ÷ 3 = <tspan font-weight="bold">100 cm³</tspan></text>
  ${ligne(198, `V = (<tspan fill="${BLEU}" font-weight="bold">aire de la base</tspan> × <tspan fill="${GRIS}" font-weight="bold">hauteur</tspan>) ÷ 3`, 15)}
  ${ligne(224, 'La pyramide remplit le tiers du pavé de même base et même hauteur.', 12)}
  ${ligne(246, 'Je n’oublie pas le ÷ 3 : sans lui, c’est le pavé.', 12, GRIS)}`,
    262,
);

// ─── Page 2 : le cylindre et le cône ───────────────────────────────────────
// Cylindre un peu plus haut et ellipses plus ouvertes (revue prof 2026-09-19) : r = 10 tient dans le disque
// sans toucher son contour ; la moitié cachée du disque de base est en pointillés.
const CX = 120; const RX = 62; const RY = 22; const HAUT = 86; const BAS = 152;
const CYLINDRE = [
    `<ellipse cx="${CX}" cy="${BAS}" rx="${RX}" ry="${RY}" fill="${BLEU_CLAIR}" stroke="none"/>`,
    `<path d="M ${CX - RX} ${BAS} A ${RX} ${RY} 0 0 1 ${CX + RX} ${BAS}" fill="none" stroke="${ENCRE}" stroke-width="1.4" stroke-dasharray="4 3"/>`,   // moitié cachée
    `<path d="M ${CX - RX} ${BAS} A ${RX} ${RY} 0 0 0 ${CX + RX} ${BAS}" fill="none" stroke="${ENCRE}" stroke-width="1.4"/>`,                            // moitié visible
    `<line x1="${CX - RX}" y1="${HAUT}" x2="${CX - RX}" y2="${BAS}" stroke="${ENCRE}" stroke-width="1.4"/>`,
    `<line x1="${CX + RX}" y1="${HAUT}" x2="${CX + RX}" y2="${BAS}" stroke="${ENCRE}" stroke-width="1.4"/>`,
    `<ellipse cx="${CX}" cy="${HAUT}" rx="${RX}" ry="${RY}" fill="none" stroke="${ENCRE}" stroke-width="1.4"/>`,
    `<line x1="${CX - RX}" y1="${BAS}" x2="${CX}" y2="${HAUT}" stroke="${ORANGE}" stroke-width="1.8"/>`,
    `<line x1="${CX + RX}" y1="${BAS}" x2="${CX}" y2="${HAUT}" stroke="${ORANGE}" stroke-width="1.8"/>`,
    `<circle cx="${CX}" cy="${HAUT}" r="3" fill="${ORANGE}"/>`,
    `<line x1="${CX}" y1="${BAS}" x2="${CX + RX}" y2="${BAS}" stroke="${BLEU}" stroke-width="1.8"/>`,
    `<text x="${CX + RX / 2}" y="${BAS - 5}" font-size="12" font-weight="bold" text-anchor="middle" fill="${BLEU}">r = 10</text>   <!-- au-dessus du rayon, dans le disque : sous lui, il chevauchait le bord de l'ellipse (revue prof 2026-09-19) -->`,
    `<text x="${CX + RX + 8}" y="${(HAUT + BAS) / 2 + 4}" font-size="12" font-weight="bold" fill="${GRIS}">3</text>`,
].join('\n  ');

const SVG_CYLINDRE = svg(
    'Un cylindre de rayon 10 et de hauteur 3, et dedans un cône orange qui a la même base et la même hauteur. Le cylindre : pi fois 10 fois 10 fois 3 égale 300 pi centimètres cubes. Le cône en prend le tiers : 100 pi centimètres cubes. Volume du cône égale pi fois rayon fois rayon fois hauteur divisé par 3.',
    `${CYLINDRE}
  <text x="236" y="90" font-size="12" fill="${ENCRE}">le cylindre :</text>
  <text x="236" y="106" font-size="12" fill="${ENCRE}">π × <tspan fill="${BLEU}" font-weight="bold">10 × 10</tspan> × <tspan fill="${GRIS}" font-weight="bold">3</tspan> = 300π cm³</text>
  <text x="236" y="134" font-size="12" fill="${ORANGE}">le cône : <tspan font-weight="bold">le tiers</tspan></text>
  <text x="236" y="150" font-size="12" fill="${ORANGE}">300π ÷ 3 = <tspan font-weight="bold">100π cm³</tspan></text>
  ${ligne(198, `V = (π × <tspan fill="${BLEU}" font-weight="bold">r × r</tspan> × <tspan fill="${GRIS}" font-weight="bold">hauteur</tspan>) ÷ 3`, 15)}
  ${ligne(224, 'Le cône remplit le tiers du cylindre de même base et même hauteur.', 12)}
  ${ligne(246, 'π × r × r, c’est l’aire du disque de base.', 12, GRIS)}`,
    262,
);

const AIDE = {
    pages: [
        { titre: 'Le tiers du pavé', svg: SVG_PAVE },
        { titre: 'Le tiers du cylindre', svg: SVG_CYLINDRE },
    ],
};

const CP_1241 = {
    pyramide_saisie: AIDE,
    cone_texte_trou_saisie: AIDE,
    calcul_qcm_unique: AIDE,
    sens_tableau_vf: AIDE,
};

export default CP_1241;
